import { faUserCircle } from '@fortawesome/pro-light-svg-icons'
import { faComment, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Typography, makeStyles } from '@material-ui/core'
import { GridColDef } from '@mui/x-data-grid'
import Avatar from '@scoir/avatar'
import { imageAsFullImageSrc } from '@scoir/avatar-image-url'

import { colors } from 'common/colors'
import AvatarIcon from 'components/AvatarIcon'

const useStyles = makeStyles({
  contactCellContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  nameCellContainer: {
    display: 'flex',
    alignItems: 'center',
    gridGap: 8,
  },
})

interface ContactInfo {
  contact: { name: string; id: string }
  highSchool: string
}

const useColumns = (onContactClick: (info: ContactInfo) => void) => {
  const classes = useStyles()

  const columns: GridColDef[] = [
    {
      field: 'highSchool',
      headerName: 'High School',
      width: 300,
    },
    {
      field: 'date',
      headerName: 'Date/Time',
      width: 300,
      valueGetter: (params) => ({
        startTime: params.row.startTime,
        endTime: params.row.endTime,
        timezone: params.row.timezone,
      }),
      valueFormatter: (params) => {
        const { startTime, endTime, timezone } = params.value
        const start = new Date(startTime)
        const end = new Date(endTime)

        const date = start
          .toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            timeZone: timezone,
          })
          .replace(',', '')
        const endTimeWithZone = end.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZone: timezone,
          timeZoneName: 'short',
        })
        const startTimeStr = start.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZone: timezone,
        })

        return `${date} ${startTimeStr} - ${endTimeWithZone}`
      },
      sortComparator: (v1, v2) => new Date(v1.startTime).getTime() - new Date(v2.startTime).getTime(),
    },
    {
      field: 'collegeFair',
      headerName: 'Type',
      width: 150,
      valueFormatter: (params) => {
        return params.value ? 'College Fair' : 'High School Visit'
      },
    },
    {
      field: 'virtual',
      headerName: 'Format',
      width: 150,
      valueFormatter: (params) => {
        return params.value ? 'Virtual' : 'In-Person'
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      valueFormatter: (params) => {
        switch (params.value) {
          case 'pending_state':
            return 'Pending'
          case 'confirmed_state':
            return 'Confirmed'
          default:
            return params.value
        }
      },
    },
    {
      field: 'contact',
      headerName: 'High School Contact',
      width: 200,
      valueFormatter: (params) => {
        const contact = params.value
        return contact?.name?.trim() ? contact.name : 'No Contact'
      },
      renderCell: (params) => {
        const contact = params.value
        if (!contact.name.trim()) {
          return 'No Contact'
        }
        return (
          <div className={classes.contactCellContainer}>
            <Button
              color="primary"
              onClick={(e) => {
                e.stopPropagation()
                onContactClick({ contact, highSchool: params.row.highSchool })
              }}
              startIcon={
                <FontAwesomeIcon
                  icon={faComment}
                  color={colors.primary.main}
                />
              }
            >
              {contact.name}
            </Button>
          </div>
        )
      },
      sortComparator: (v1, v2) => {
        const name1 = v1?.name.split(' ') ?? ''
        const name2 = v2?.name.split(' ') ?? ''
        const first1 = name1[0] ?? ''
        const first2 = name2[0] ?? ''
        const last1 = name1[1] ?? ''
        const last2 = name2[1] ?? ''
        return lastNameSortComparator(last1, last2, first1, first2)
      },
    },
    {
      field: 'rep',
      headerName: 'Admissions Rep',
      width: 200,
      valueFormatter: (params) => {
        const rep = params.value
        return rep?.deleted ? 'Deleted User' : `${rep?.firstName ?? ''} ${rep?.lastName ?? ''}`.trim()
      },
      renderCell: (params) => {
        const rep = params.row.rep
        const avatarUrl = rep.avatarUrl ?? ''
        return (
          <div className={classes.nameCellContainer}>
            <Avatar
              aria-label="user icon"
              variant="circular"
              size="sm"
              sourceUrl={imageAsFullImageSrc(avatarUrl)}
              fallbackIcon={
                rep.deleted ? (
                  <AvatarIcon
                    icon={faExclamationTriangle}
                    color={colors.warning.main}
                  />
                ) : (
                  <FontAwesomeIcon
                    size="2xl"
                    icon={faUserCircle}
                  />
                )
              }
            />
            <Typography variant="body2">{rep.deleted ? 'Deleted User' : `${rep.firstName} ${rep.lastName}`}</Typography>
          </div>
        )
      },
      sortComparator: (v1, v2) => {
        const last1 = v1?.lastName ?? ''
        const last2 = v2?.lastName ?? ''
        const first1 = v1?.firstName ?? ''
        const first2 = v2?.firstName ?? ''
        return lastNameSortComparator(last1, last2, first1, first2)
      },
    },
  ]

  return columns
}

const lastNameSortComparator = (last1: string, last2: string, first1: string, first2: string) => {
  const lastNameComparison = last2.localeCompare(last1)
  if (lastNameComparison !== 0) {
    return lastNameComparison
  }
  return first2.localeCompare(first1)
}

export default useColumns
